import chunk from 'lodash-es/chunk'
import * as martinez from 'martinez-polygon-clipping'
import type { $Plate, BendSegmentInfo, Point } from 'metalworks-domain'
import {
  CornerCountersinkHoles,
  CountersinkHole,
  Finish,
  FinishDirection,
  KickPlateCountersinkHoles,
  Model,
  RawMaterial,
  bendData,
  calculateBendLengths,
  calculateCustomBendLine,
  rawMaterialLabels,
} from 'metalworks-domain'
import type { MeshPhysicalMaterialParameters, MeshStandardMaterialParameters, Texture } from 'three'
import {
  BufferGeometry,
  ExtrudeGeometry,
  Group,
  Line,
  LineBasicMaterial,
  MeshPhysicalMaterial,
  NoColorSpace,
  RepeatWrapping,
  SRGBColorSpace,
  TextureLoader,
  Vector2,
  Vector3,
} from 'three'
import { Brush, Evaluator, SUBTRACTION } from 'three-bvh-csg'
import { LoopSubdivision } from 'three-subdivide'
import { match } from 'ts-pattern'

import { isNonNullish } from '@salsita-ai/platform-common/utils'
import { type SwatchOption } from '@salsita-ai/platform-project-sdk/domain'

import { createCountersinkHoleMesh } from './countersink-hole.js'
import { createCountersinkHoleMeshes } from './multple-fixing-holes.js'
import { createBendShape, createCircleShape, createRoundedRectShape, createTStrikeShape } from './shapes.js'
import { csgCombineMeshes, polygonsToPaths } from './utils.js'

const textureLoader = new TextureLoader()

const createTextureLoadHandler =
  (rotation: number, isColorTexture: boolean = false) =>
  (texture: Texture) => {
    texture.wrapS = texture.wrapT = RepeatWrapping
    texture.repeat.set(0.01, 0.01)
    texture.rotation = rotation
    texture.colorSpace = isColorTexture ? SRGBColorSpace : NoColorSpace
    return texture
  }

const materialFinishParams = {
  [Finish.brushed]: {
    [FinishDirection.horizontal]: {
      bumpMap: textureLoader.load('textures/brushed_Bump.png', createTextureLoadHandler(0)),
      bumpScale: 1,
      roughnessMap: textureLoader.load('textures/brushed_Roughness.png', createTextureLoadHandler(0)),
    } satisfies MeshStandardMaterialParameters,
    [FinishDirection.vertical]: {
      bumpMap: textureLoader.load('textures/brushed_Bump.png', createTextureLoadHandler(Math.PI / 2)),
      bumpScale: 1,
      roughnessMap: textureLoader.load('textures/brushed_Roughness.png', createTextureLoadHandler(Math.PI / 2)),
    } satisfies MeshStandardMaterialParameters,
  },
  [Finish.milled]: {
    bumpMap: textureLoader.load('textures/milled_Bump.png', createTextureLoadHandler(0)),
    bumpScale: 1.5,
    roughnessMap: textureLoader.load('textures/milled_Roughness.png', createTextureLoadHandler(0)),
    roughness: 1,
  } satisfies MeshStandardMaterialParameters,
  [Finish.chequer]: {
    roughnessMap: textureLoader.load('textures/chequer_Roughness.png', createTextureLoadHandler(0)),
    roughness: 1,
    normalMap: textureLoader.load('textures/chequer_Normal.png', createTextureLoadHandler(0)),
    normalScale: new Vector2(0.25, 0.25),
  } satisfies MeshStandardMaterialParameters,
  [Finish.propeller]: {
    roughnessMap: textureLoader.load('textures/propeller_Roughness.png', createTextureLoadHandler(0)),
    roughness: 1,
    normalMap: textureLoader.load('textures/propeller_Normal.png', createTextureLoadHandler(0)),
    normalScale: new Vector2(0.25, 0.25),
  } satisfies MeshStandardMaterialParameters,
} as const

type MaterialSwatch = SwatchOption & {
  materialParams: Partial<
    Pick<
      MeshPhysicalMaterialParameters,
      'metalness' | 'roughness' | 'bumpMap' | 'bumpScale' | 'roughnessMap' | 'map' | 'envMapIntensity' | 'reflectivity'
    >
  >
}

export const materialSwatches: Record<RawMaterial, MaterialSwatch> = {
  [RawMaterial.ss304]: {
    swatchName: rawMaterialLabels[RawMaterial.ss304],
    color: '#c2c2c2',
    materialParams: {
      metalness: 1,
      roughness: 1.5,
    },
  },
  [RawMaterial.ss316]: {
    swatchName: rawMaterialLabels[RawMaterial.ss316],
    color: '#c2c2c2',
    materialParams: {
      metalness: 1,
      roughness: 1.5,
    },
  },
  [RawMaterial.aluminium]: {
    swatchName: rawMaterialLabels[RawMaterial.aluminium],
    color: '#e2e2e2',
    materialParams: {
      metalness: 0.7,
      roughness: 1,
      envMapIntensity: 1.2,
    },
  },
  [RawMaterial.brass]: {
    swatchName: rawMaterialLabels[RawMaterial.brass],
    color: '#e7dcc4',
    materialParams: {
      metalness: 1,
      roughness: 1,
      map: textureLoader.load('textures/brass_BaseColor.png', createTextureLoadHandler(0, true)),
      envMapIntensity: 1.5,
    },
  },
}

const powderCoatingMaterialOptions: Omit<MaterialSwatch, 'swatchName'> = {
  color: '#000000',
  materialParams: {
    metalness: 0.1,
    roughness: 0.5,
    reflectivity: 0.8,
  },
}

const applyPlateSegmentRotation = (mesh: Brush, segmentInfo: BendSegmentInfo) => {
  for (const rotation of segmentInfo.rotation) {
    mesh.rotateX(rotation.x ?? 0)
    mesh.rotateY(rotation.y ?? 0)
    mesh.rotateZ(rotation.z ?? 0)
  }

  mesh.position.x = segmentInfo.offset.x
  mesh.position.y = segmentInfo.offset.y
  mesh.position.z = segmentInfo.offset.z

  mesh.updateMatrixWorld()
}

type PlateData = Pick<
  $Plate,
  | '$holes'
  | '$sizeX'
  | '$sizeY'
  | 'finish'
  | 'finishDirection'
  | 'rawMaterial'
  | 'thickness'
  | 'type'
  | 'handing'
  | 'powderCoating'
>

export interface SegmentRotation {
  x: number
  y: number
}

const createPlateSegment = (
  segmentType: 'start' | 'end' | 'flat',
  plate: PlateData,
  plateMaterial: MeshPhysicalMaterial,
  segmentInfo: BendSegmentInfo
) => {
  try {
    // TODO: This should be an analytics event, but there's no type for this kind of event yet.
    // eslint-disable-next-line no-console
    console.time('createSegment')

    const { $holes, finish, finishDirection, rawMaterial, thickness, type, handing, powderCoating, $sizeX, $sizeY } =
      plate

    const segment = {
      $holes,
      finish,
      finishDirection,
      rawMaterial,
      thickness,
      type,
      handing,
      powderCoating,
      $sizeX,
      $sizeY,
    }

    const plateShape = match(segmentInfo)
      .with({ shape: { type: 'circle' } }, segment => createCircleShape(segment))
      .with({ shape: { type: 'roundedRect' } }, segment => createRoundedRectShape(segment))
      .with({ shape: { type: 'tStrike' } }, segment => createTStrikeShape(segment))
      .otherwise(() => null)

    if (!plateShape) {
      return null
    }

    // Create feature polygons for extrusion.
    const featurePolygons: martinez.Geometry[] = []
    for (const feature of $holes) {
      const isCornerHole = Model.CornerFixingHoles.is(feature)
      const isPlacedInsideSegment =
        feature.$absCoords.x >= segmentInfo.start.x &&
        feature.$absCoords.x <= segmentInfo.end.x &&
        feature.$absCoords.y >= segmentInfo.start.y &&
        feature.$absCoords.y <= segmentInfo.end.y

      if (isCornerHole || isPlacedInsideSegment) {
        featurePolygons.push(...feature.$polygons)
      }
    }

    if (featurePolygons.length > 0) {
      // Optimize polygons by getting rid of overlapping areas. Overlapping might also cause rendering glitches.
      const mergedFeaturePolygons = featurePolygons.reduce<martinez.Geometry>(martinez.union, [[[]]])
      const intersectedFeaturePolygons = martinez.intersection(mergedFeaturePolygons, [
        [
          [segmentInfo.start.x, segmentInfo.start.y],
          [segmentInfo.end.x, segmentInfo.start.y],
          [segmentInfo.end.x, segmentInfo.end.y],
          [segmentInfo.start.x, segmentInfo.end.y],
        ],
      ])

      // Convert optimized polygons to Three.js paths and use them as holes in the plate.
      plateShape.holes = polygonsToPaths(intersectedFeaturePolygons)
    }

    // Extrude the whole plate with holes into final thickness.
    const plateGeometry = new ExtrudeGeometry(plateShape, {
      depth: thickness,
      bevelEnabled: false,
    })

    const plateMesh = new Brush(plateGeometry, plateMaterial)

    const meshesToRemove: Brush[] = []
    for (const feature of $holes) {
      if (CountersinkHole.is(feature)) {
        meshesToRemove.push(createCountersinkHoleMesh(feature, thickness, handing, plateMaterial))
      } else if (CornerCountersinkHoles.is(feature)) {
        meshesToRemove.push(...createCountersinkHoleMeshes(feature, segment, plateMaterial, { horizontal: 4 }))
      } else if (KickPlateCountersinkHoles.is(feature)) {
        meshesToRemove.push(
          ...createCountersinkHoleMeshes(feature, segment, plateMaterial, {
            horizontal: feature.$horizontalCount,
            vertical: feature.$verticalCount,
          })
        )
      }
    }

    if (meshesToRemove.length === 0) {
      if (segmentType === 'end') {
        applyPlateSegmentRotation(plateMesh, segmentInfo)
      }

      return plateMesh
    }

    // Adding features to the same mesh gets gradually slower. Using multiple smaller
    // meshes instead of one big mesh improves performance and the difference increases with
    // number of features. Using square root distributes the operations evenly.
    const chunkSize = Math.ceil(Math.sqrt(meshesToRemove.length))
    const featureChunks = chunk(meshesToRemove, chunkSize)
    const csgEvaluator = new Evaluator()
    const extraMaterialMesh = csgCombineMeshes(
      csgEvaluator,
      featureChunks.map(chunk => csgCombineMeshes(csgEvaluator, chunk))
    )

    const finalMesh = csgEvaluator.evaluate(plateMesh, extraMaterialMesh, SUBTRACTION)

    if (segmentType === 'end') {
      applyPlateSegmentRotation(finalMesh, segmentInfo)
    }

    return finalMesh
  } finally {
    // eslint-disable-next-line no-console
    console.timeEnd('createSegment')
  }
}

const createBentSegment = (_plate: PlateData, plateMaterial: MeshPhysicalMaterial, segmentInfo: BendSegmentInfo) => {
  const segmentShape = segmentInfo.shape
  if (segmentShape.type !== 'bend') {
    return null
  }

  const cornerRadius = segmentShape.cornerRadius
  const requiresMeshOperations =
    cornerRadius.topLeft > 0 || cornerRadius.topRight > 0 || cornerRadius.bottomLeft > 0 || cornerRadius.bottomRight > 0

  const geometry = requiresMeshOperations ? createRealBentSegment(segmentInfo) : createExtrudedBentSegment(segmentInfo)

  for (const rotation of segmentInfo.rotation) {
    geometry.rotateY(rotation.y ?? 0)
    geometry.rotateX(rotation.x ?? 0)
    geometry.rotateZ(rotation.z ?? 0)
  }

  geometry.translate(segmentInfo.offset.x, segmentInfo.offset.y, segmentInfo.offset.z)

  const mesh = new Brush(geometry, plateMaterial)

  return mesh
}

const createExtrudedBentSegment = (segmentInfo: BendSegmentInfo) => {
  const segmentShape = segmentInfo.shape
  if (segmentShape.type !== 'bend') {
    throw new Error('Segment is not a bend')
  }

  const shape = createBendShape(
    segmentInfo.start,
    segmentInfo.bend.orientation,
    segmentInfo.bend.direction,
    segmentInfo.bend.innerRadius,
    segmentInfo.plate.thickness
  )

  const depth =
    segmentInfo.bend.orientation === Model.BendOrientation.Horizontal
      ? segmentInfo.end.x - segmentInfo.start.x
      : segmentInfo.end.y - segmentInfo.start.y

  const geometry = new ExtrudeGeometry(shape, {
    depth,
    bevelEnabled: false,
  })

  if (segmentInfo.bend.orientation === Model.BendOrientation.Horizontal) {
    geometry.rotateY(Math.PI / 2)
  } else {
    geometry.rotateX(-Math.PI / 2)
  }

  return geometry
}

const createRealBentSegment = (segmentInfo: BendSegmentInfo) => {
  const segmentShape = segmentInfo.shape
  if (segmentShape.type !== 'bend') {
    throw new Error('Segment is not a bend')
  }

  // Start/end coordinates might be switched as a way how to enforce
  // rendering direction from right to left - this is important for bends.
  const sanitizedStart = {
    x: Math.min(segmentInfo.start.x, segmentInfo.end.x),
    y: Math.min(segmentInfo.start.y, segmentInfo.end.y),
  }

  const sanitizedEnd = {
    x: Math.max(segmentInfo.start.x, segmentInfo.end.x),
    y: Math.max(segmentInfo.start.y, segmentInfo.end.y),
  }

  const shape = createRoundedRectShape({
    start: sanitizedStart,
    end: sanitizedEnd,
    shape: {
      type: 'roundedRect',
      cornerRadius: segmentShape.cornerRadius,
    },
  })

  const geometry = new ExtrudeGeometry(shape, {
    depth: segmentInfo.plate.thickness,
    bevelEnabled: false,
  })

  const bendableGeometry = LoopSubdivision.modify(geometry, 3, {
    flatOnly: true,
    split: true,
    uvSmooth: true,
    preserveEdges: true,
    maxTriangles: Infinity,
  })

  const thickness = segmentInfo.plate.thickness
  const bendTool = bendData[segmentInfo.bend.bendType].getTool(thickness)
  if (!bendTool) {
    throw new Error('Bend tool not found')
  }

  const pos = bendableGeometry.getAttribute('position')

  const { neutralRadius } = calculateBendLengths(
    segmentInfo.bend.innerRadius,
    segmentInfo.bend.innerRadius + thickness,
    segmentInfo.bend.angle.to,
    segmentInfo.plate.rawMaterial,
    bendTool.tailLength
  )

  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i)
    const y = pos.getY(i)
    const z = pos.getZ(i)

    const bendRadius = match(segmentInfo.bend.direction)
      .with(Model.BendDirection.Front, () => segmentInfo.bend.innerRadius + z)
      .with(Model.BendDirection.Back, () => segmentInfo.bend.innerRadius + thickness - z)
      .exhaustive()

    const {
      x: newX,
      y: newY,
      z: newZ,
    } = calculateCustomBendLine(
      bendRadius,
      neutralRadius,
      segmentInfo.bend.angle.to,
      segmentInfo.bend.direction,
      { x: segmentInfo.start.x, y: segmentInfo.start.y, z },
      { x, y, z }
    )

    pos.setX(i, newX)
    pos.setY(i, newY)
    pos.setZ(i, newZ)
  }

  pos.needsUpdate = true
  bendableGeometry.computeVertexNormals()

  return bendableGeometry
}

const createSegmentLine = (from: Point, to: Point, thickness: number) => {
  const lineGeometry = new BufferGeometry()
  const lineMaterial = new LineBasicMaterial({ color: 0x000000 })
  const safeThickness = thickness + 0.1

  const startPoint = new Vector3(from.x, from.y, safeThickness)
  const endPoint = new Vector3(to.x, to.y, safeThickness)

  lineGeometry.setFromPoints([startPoint, endPoint])

  return new Line(lineGeometry, lineMaterial)
}

export const createPlate = (plate: $Plate, is3D: boolean) => {
  try {
    // TODO: This should be an analytics event, but there's no type for this kind of event yet.
    // eslint-disable-next-line no-console
    console.time('createPlate')

    const { powderCoating, rawMaterial, finish, finishDirection } = plate

    let color: string
    let materialParams: MeshStandardMaterialParameters

    if (powderCoating) {
      color = powderCoatingMaterialOptions.color
      materialParams = powderCoatingMaterialOptions.materialParams
    } else {
      const selectedMaterial = materialSwatches[rawMaterial]

      color = selectedMaterial.color
      materialParams = {
        ...selectedMaterial.materialParams,
        ...(finish === Finish.brushed ? materialFinishParams[finish][finishDirection] : materialFinishParams[finish]),
      }
    }

    const plateMaterial = new MeshPhysicalMaterial({ color, ...materialParams })
    const segments = plate.$segmentInfo
    const bendPlateSegments = [
      is3D && segments.start ? createPlateSegment('start', plate, plateMaterial, segments.start) : null,
      is3D && segments.end ? createPlateSegment('end', plate, plateMaterial, segments.end) : null,
      is3D && segments.bend ? createBentSegment(plate, plateMaterial, segments.bend) : null,
    ].filter(isNonNullish)

    const plateSegments = [
      ...bendPlateSegments,
      // Unfolded "2D" view or fallback for non-bent plates.
      (!is3D || bendPlateSegments.length === 0) && segments.flat
        ? createPlateSegment('flat', plate, plateMaterial, segments.flat)
        : null,
    ].filter(isNonNullish)

    const csgEvaluator = new Evaluator()
    const finalMesh = plateSegments.length === 0 ? plateSegments[0]! : csgCombineMeshes(csgEvaluator, plateSegments)

    // Disable raycasting for the final mesh to improve performance.
    finalMesh.raycast = () => {}

    // Create a group to contain the mesh and lines
    const plateGroup = new Group()
    plateGroup.add(finalMesh)

    // Add lines to mark segment boundaries in 2D view.
    if (!is3D && segments.start && segments.end) {
      const startSegmentLine = createSegmentLine(
        segments.start.bend.orientation === Model.BendOrientation.Horizontal
          ? { x: segments.start.start.x, y: segments.start.end.y }
          : { x: segments.start.end.x, y: segments.start.start.y },
        { x: segments.start.end.x, y: segments.start.end.y },
        segments.start.plate.thickness
      )
      const endSegmentLine = createSegmentLine(
        { x: segments.end.start.x, y: segments.end.start.y },
        segments.end.bend.orientation === Model.BendOrientation.Horizontal
          ? { x: segments.end.end.x, y: segments.end.start.y }
          : { x: segments.end.start.x, y: segments.end.end.y },
        segments.end.plate.thickness
      )

      plateGroup.add(startSegmentLine)
      plateGroup.add(endSegmentLine)
    }

    return plateGroup
  } finally {
    // eslint-disable-next-line no-console
    console.timeEnd('createPlate')
  }
}
